import { Edge, Node } from '@xyflow/react';
import { useMemo } from 'react';
import { MindNodeEmun } from '../components/const';

function isNodeExpanded(node: Node, globalExpanded?: boolean): boolean {
  return globalExpanded !== undefined ? globalExpanded : !!node.data?.expanded;
}

function addAllDescendants(
  nodeId: string,
  visibleNodeIds: Set<string>,
  edges: Edge[],
  nodeMap: Map<string, Node>,
  globalExpanded?: boolean
) {
  edges.forEach(edge => {
    if (edge.source === nodeId && !visibleNodeIds.has(edge.target)) {
      visibleNodeIds.add(edge.target);
      const descendantNode = nodeMap.get(edge.target);
      if (descendantNode && isNodeExpanded(descendantNode, globalExpanded)) {
        addAllDescendants(
          edge.target,
          visibleNodeIds,
          edges,
          nodeMap,
          globalExpanded
        );
      }
    }
  });
}

export const useExpandCollapse = (
  nodes: Node[],
  edges: Edge[],
  globalExpanded?: boolean
) => {
  const { visibleNodes, visibleEdges } = useMemo(() => {
    // Build a lookup map for nodes.
    const nodeMap = new Map(nodes.map(node => [node.id, node]));
    const visibleNodeIds = new Set<string>();

    // Always show the parent node.
    const parentId = MindNodeEmun.PARENT;
    visibleNodeIds.add(parentId);

    // Process first-layer nodes: those directly connected to the parent.
    edges.forEach(edge => {
      if (edge.source === parentId) {
        visibleNodeIds.add(edge.target);
        const childNode = nodeMap.get(edge.target);
        if (childNode && isNodeExpanded(childNode, globalExpanded)) {
          addAllDescendants(
            edge.target,
            visibleNodeIds,
            edges,
            nodeMap,
            globalExpanded
          );
        }
      }
    });

    // Filter nodes and edges based on the visible node IDs.
    const visibleNodes = nodes.filter(node => visibleNodeIds.has(node.id));
    const visibleEdges = edges.filter(
      edge => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
    );

    return { visibleNodes, visibleEdges };
  }, [nodes, edges, globalExpanded]);

  return { nodes: visibleNodes, edges: visibleEdges };
};
